import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Lock, MessageCircle, Phone, SquarePen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { UserSearchModal } from "@/components/UserSearchModal";
import { api } from "@/App";
import { useLang } from "@/i18n";
import { E2ERestoreBanner } from "@/components/E2ERestoreBanner";
import { ensureKeyPair, deriveSharedKey, decryptText } from "@/utils/e2e";

export default function Messages() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [previews, setPreviews] = useState({});
  const [myId, setMyId] = useState(null);
  const [needsRestore, setNeedsRestore] = useState(false);
  const [missedCount, setMissedCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const decryptPreviews = async (priv, convs) => {
    const decrypted = {};
    for (const conv of convs) {
      const msg = conv.last_message;
      if (msg?.encrypted && msg?.iv && conv.user?.e2e_public_key) {
        const key = await deriveSharedKey(priv, conv.user.e2e_public_key);
        const text = await decryptText(key, msg.content, msg.iv);
        if (text) decrypted[conv.user.user_id] = text;
      }
    }
    setPreviews(decrypted);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, convRes] = await Promise.all([
          api.get("/auth/me"),
          api.get("/chat/conversations"),
        ]);
        const convs = convRes.data.conversations || [];
        setMyId(meRes.data.user_id);
        setConversations(convs);
        setLoading(false);

        // Decrypt encrypted previews
        try {
          const kp = await ensureKeyPair(meRes.data.user_id);
          if (!kp) {
            setNeedsRestore(true);
            return;
          }
          await decryptPreviews(kp.priv, convs);
        } catch (e) {
          console.error("E2E preview decrypt failed", e);
        }
      } catch (e) {
        setLoading(false);
      }
    };
    load();
    api.get("/calls/missed/unseen-count")
      .then((r) => setMissedCount(r.data.count || 0))
      .catch(() => {});
  }, []);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return t("justNow") || "adesso";
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}g`;
    return date.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  };

  const getPreview = (conv) => {
    const msg = conv.last_message;
    if (!msg) return "";
    if (msg.deleted_for_everyone) return t("deletedTombstone");
    const prefix = msg.from_user_id === myId ? t("youPrefix") : "";
    if (msg.encrypted) {
      const text = previews[conv.user.user_id];
      return text ? prefix + text : t("encryptedMessage");
    }
    return prefix + msg.content;
  };

  const handleDeleteConversation = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/chat/conversation/${deleteTarget.user_id}`);
      setConversations((prev) => prev.filter((c) => c.user.user_id !== deleteTarget.user_id));
      toast.success(t("conversationDeleted"));
    } catch {
      toast.error(t("messageDeleteError"));
    } finally {
      setDeleteTarget(null);
    }
  };

  const getPictureUrl = (picture) => {
    if (!picture) return null;
    return picture.startsWith("/api")
      ? `${process.env.REACT_APP_BACKEND_URL}${picture}`
      : picture;
  };

  return (
    <div className="page-content bg-gradient-to-b from-white via-white to-gray-50/50" data-testid="messages-page">
      {/* Header */}
      <div className="sticky top-0 safe-top z-40 bg-white border-b border-gray-100/50 shadow-sm">
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 bg-clip-text text-transparent">
                {t("messagesTitle")}
              </h1>
              <div className="flex items-center gap-1 mt-0.5" data-testid="e2e-banner">
                <Lock className="w-2.5 h-2.5 text-emerald-500" strokeWidth={2.5} />
                <span className="text-[10px] text-gray-500 font-medium tracking-wide">{t("e2eProtected")}</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/app/calls')}
              className="relative w-11 h-11 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center shadow-lg shadow-orange-200/50 active:scale-95 transition-transform"
              data-testid="call-history-btn"
            >
              <Phone className="w-5 h-5 text-white" strokeWidth={2} />
              {missedCount > 0 && (
                <span
                  className="absolute top-0 right-0 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white"
                  data-testid="missed-calls-badge"
                />
              )}
            </button>
            <button
              onClick={() => setShowNewChat(true)}
              className="w-11 h-11 ml-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center shadow-lg shadow-orange-200/50 active:scale-95 transition-transform"
              data-testid="new-chat-btn"
            >
              <SquarePen className="w-5 h-5 text-white" strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      {/* E2E Restore banner (new device) */}
      {needsRestore && myId && (
        <E2ERestoreBanner
          userId={myId}
          onRestored={async (kp) => {
            setNeedsRestore(false);
            await decryptPreviews(kp.priv, conversations);
          }}
        />
      )}

      {/* Conversations */}
      {loading ? (
        <div className="px-4 py-4 space-y-4" data-testid="messages-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-12 h-12 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-32 rounded bg-gray-200" />
                <div className="h-2.5 w-48 rounded bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 px-4" data-testid="messages-empty">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100 flex items-center justify-center mb-5">
            <MessageCircle className="w-9 h-9 text-pink-400" strokeWidth={1.5} />
          </div>
          <p className="text-gray-700 font-medium mb-1">{t("noConversations")}</p>
          <p className="text-sm text-gray-400 text-center">{t("noConversationsHint")}</p>
        </div>
      ) : (
        <div className="msg-glass-list">
          {conversations.map((conv) => {
            const unread = conv.unread_count > 0;
            return (
              <div
                key={conv.user.user_id}
                className="msg-glass-row w-full flex items-center gap-3 px-3 py-3 text-left"
                data-testid={`conversation-${conv.user.user_id}`}
              >
                <button
                  onClick={() => navigate(`/app/chat/${conv.user.user_id}`)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  data-testid={`open-conversation-${conv.user.user_id}`}
                >
                <Avatar className={`w-12 h-12 flex-shrink-0 ${conv.user.is_verified ? "ring-2 ring-offset-1 ring-[#D4A574]" : ""}`}>
                  {conv.user.picture ? (
                    <AvatarImage src={getPictureUrl(conv.user.picture)} alt={conv.user.name} />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-orange-100 to-pink-100 text-gray-500">
                      {conv.user.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${unread ? "font-bold text-gray-900" : "font-medium text-gray-800"}`}>
                      {conv.user.name}
                    </span>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {formatTime(conv.last_message?.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className={`text-xs truncate ${unread ? "text-gray-800 font-medium" : "text-gray-400"}`}>
                      {getPreview(conv)}
                    </p>
                    {unread && (
                      <span
                        className="min-w-[18px] h-[18px] flex items-center justify-center bg-gradient-to-r from-orange-500 to-pink-500 text-white text-[10px] font-bold rounded-full px-1 flex-shrink-0"
                        data-testid={`unread-badge-${conv.user.user_id}`}
                      >
                        {conv.unread_count > 99 ? "99+" : conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
                </button>
                <button
                  onClick={() => setDeleteTarget(conv.user)}
                  className="p-2 rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
                  data-testid={`delete-conversation-btn-${conv.user.user_id}`}
                  aria-label="Elimina conversazione"
                >
                  <Trash2 className="w-4 h-4 text-gray-300 hover:text-red-400" strokeWidth={1.5} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete conversation confirmation */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-end"
          onClick={() => setDeleteTarget(null)}
          data-testid="delete-conversation-overlay"
        >
          <div
            className="w-full bg-white rounded-t-3xl p-5 pb-8 safe-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900 mb-1">{t("deleteConversation")}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t("deleteConversationDesc")}
            </p>
            <button
              onClick={handleDeleteConversation}
              className="w-full py-3 rounded-xl bg-red-500 text-white text-sm font-semibold active:scale-[0.98] transition-transform mb-2"
              data-testid="confirm-delete-conversation-btn"
            >
              {t("deleteConversation")}
            </button>
            <button
              onClick={() => setDeleteTarget(null)}
              className="w-full py-3 rounded-xl text-gray-500 text-sm"
              data-testid="cancel-delete-conversation-btn"
            >
              {t("cancelAction")}
            </button>
          </div>
        </div>
      )}

      {/* New conversation: search a user and open the chat directly */}
      <UserSearchModal
        open={showNewChat}
        onClose={() => setShowNewChat(false)}
        onSelect={(u) => { setShowNewChat(false); navigate(`/app/chat/${u.user_id}`); }}
        title="Nuovo messaggio"
      />
    </div>
  );
}
